import { useState } from 'react';
import type { GeoEntityDto } from '../../lib/api-types';
import type { GeoCreateInput, GeoEntityKind, GeoListParams } from '../../lib/api';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { ConfirmDialog, Dialog } from '../../components/ui/Dialog';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/cn';
import {
  useCreateGeoEntity,
  useGeoEntities,
  useSetGeoEntityActive,
  useUpdateGeoEntity,
} from './hooks';
import { CodeNameForm } from './shared';

/**
 * « Géographie » : panneaux en cascade Pays → Villes → Quartiers, plus les
 * catégories de zone du pays. Aucune suppression physique : uniquement
 * activation / désactivation, confirmées explicitement.
 */
export function GeographieTab(): JSX.Element {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [countryId, setCountryId] = useState('');
  const [cityId, setCityId] = useState('');

  const inactiveParam = includeInactive ? true : undefined;
  const zonesQuery = useGeoEntities(
    'zone-categories',
    { countryId, includeInactive: inactiveParam },
    countryId !== '',
  );

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={includeInactive}
          onChange={(e) => setIncludeInactive(e.target.checked)}
        />
        Afficher les entrées désactivées
      </label>

      <div className="grid gap-4 lg:grid-cols-2">
        <GeoPanel
          kind="countries"
          title="Pays"
          addLabel="Ajouter un pays"
          params={{ includeInactive: inactiveParam }}
          selectable
          selectedId={countryId}
          onSelect={(id) => {
            setCountryId(id);
            setCityId('');
          }}
          buildCreateInput={(values) => values}
        />

        <GeoPanel
          kind="cities"
          title="Villes"
          addLabel="Ajouter une ville"
          params={{ countryId, includeInactive: inactiveParam }}
          enabled={countryId !== ''}
          disabledHint="Sélectionnez un pays pour gérer ses villes."
          selectable
          selectedId={cityId}
          onSelect={setCityId}
          buildCreateInput={(values) => ({ ...values, countryId })}
        />

        <GeoPanel
          kind="districts"
          title="Quartiers"
          addLabel="Ajouter un quartier"
          params={{ cityId, includeInactive: inactiveParam }}
          enabled={cityId !== ''}
          disabledHint="Sélectionnez une ville pour gérer ses quartiers."
          buildCreateInput={(values, extras) => ({
            ...values,
            cityId,
            zoneCategoryId: extras.zoneCategoryId || undefined,
          })}
          zoneCategories={zonesQuery.data ?? []}
        />

        <GeoPanel
          kind="zone-categories"
          title="Catégories de zone"
          addLabel="Ajouter une catégorie de zone"
          params={{ countryId, includeInactive: inactiveParam }}
          enabled={countryId !== ''}
          disabledHint="Sélectionnez un pays pour gérer ses catégories de zone."
          buildCreateInput={(values) => ({ ...values, countryId })}
        />
      </div>
    </div>
  );
}

function GeoPanel({
  kind,
  title,
  addLabel,
  params,
  enabled = true,
  disabledHint,
  selectable = false,
  selectedId,
  onSelect,
  buildCreateInput,
  zoneCategories,
}: {
  kind: GeoEntityKind;
  title: string;
  addLabel: string;
  params: GeoListParams;
  enabled?: boolean;
  disabledHint?: string;
  selectable?: boolean;
  selectedId?: string;
  onSelect?: (id: string) => void;
  buildCreateInput: (
    values: { code: string; name: string },
    extras: { zoneCategoryId: string },
  ) => GeoCreateInput;
  zoneCategories?: GeoEntityDto[];
}): JSX.Element {
  const query = useGeoEntities(kind, params, enabled);
  const create = useCreateGeoEntity(kind);
  const update = useUpdateGeoEntity(kind);
  const setActive = useSetGeoEntityActive(kind);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<GeoEntityDto | null>(null);
  const [toggling, setToggling] = useState<GeoEntityDto | null>(null);
  const [zoneCategoryId, setZoneCategoryId] = useState('');

  const openCreate = () => {
    create.reset();
    setZoneCategoryId('');
    setCreating(true);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>{title}</CardTitle>
          <Button size="sm" onClick={openCreate} disabled={!enabled}>
            {addLabel}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {setActive.isError ? (
          <Alert variant="error" details={toDisplayError(setActive.error).details}>
            {toDisplayError(setActive.error).message}
          </Alert>
        ) : null}

        {!enabled ? (
          <p className="text-sm text-slate-500">{disabledHint}</p>
        ) : query.isLoading ? (
          <Spinner label={`Chargement — ${title}…`} />
        ) : query.isError ? (
          <Alert variant="error">{toDisplayError(query.error).message}</Alert>
        ) : query.data && query.data.length > 0 ? (
          <ul className="divide-y divide-slate-100">
            {query.data.map((entity) => (
              <li key={entity.id} className="flex flex-wrap items-center gap-2 py-2">
                {selectable ? (
                  <button
                    type="button"
                    aria-pressed={selectedId === entity.id}
                    onClick={() => onSelect?.(entity.id)}
                    className={cn(
                      'rounded px-1 text-sm font-medium hover:underline',
                      selectedId === entity.id ? 'text-brand-700' : 'text-slate-800',
                    )}
                  >
                    {entity.name}
                  </button>
                ) : (
                  <span className="text-sm font-medium text-slate-800">{entity.name}</span>
                )}
                <span className="font-mono text-xs text-slate-500">{entity.code}</span>
                {!entity.isActive ? <Badge tone="danger">Inactif</Badge> : null}
                <span className="ml-auto flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Modifier ${entity.name}`}
                    onClick={() => {
                      update.reset();
                      setEditing(entity);
                    }}
                  >
                    Modifier
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`${entity.isActive ? 'Désactiver' : 'Activer'} ${entity.name}`}
                    onClick={() => setToggling(entity)}
                  >
                    {entity.isActive ? 'Désactiver' : 'Activer'}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Aucune entrée pour le moment.</p>
        )}
      </CardContent>

      {/* Create dialog */}
      <Dialog open={creating} onClose={() => setCreating(false)} title={addLabel}>
        <div className="space-y-4">
          {create.isError ? (
            <Alert variant="error" details={toDisplayError(create.error).details}>
              {toDisplayError(create.error).message}
            </Alert>
          ) : null}
          <CodeNameForm
            idPrefix={`${kind}-create`}
            pending={create.isPending}
            onCancel={() => setCreating(false)}
            onSubmit={(values) =>
              create.mutate(buildCreateInput(values, { zoneCategoryId }), {
                onSuccess: () => setCreating(false),
              })
            }
          >
            {zoneCategories ? (
              <div>
                <Label htmlFor={`${kind}-create-zone`}>Catégorie de zone (facultatif)</Label>
                <Select
                  id={`${kind}-create-zone`}
                  value={zoneCategoryId}
                  onChange={(e) => setZoneCategoryId(e.target.value)}
                >
                  <option value="">— Aucune —</option>
                  {zoneCategories.map((zc) => (
                    <option key={zc.id} value={zc.id}>
                      {zc.name} ({zc.code})
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
          </CodeNameForm>
        </div>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing ? `Modifier « ${editing.name} »` : ''}
      >
        {editing ? (
          <div className="space-y-4">
            {update.isError ? (
              <Alert variant="error" details={toDisplayError(update.error).details}>
                {toDisplayError(update.error).message}
              </Alert>
            ) : null}
            <CodeNameForm
              idPrefix={`${kind}-edit`}
              initial={{ code: editing.code, name: editing.name }}
              submitLabel="Enregistrer"
              pending={update.isPending}
              onCancel={() => setEditing(null)}
              onSubmit={(values) =>
                update.mutate(
                  { id: editing.id, input: values },
                  { onSuccess: () => setEditing(null) },
                )
              }
            />
          </div>
        ) : null}
      </Dialog>

      {/* Activate / deactivate confirmation */}
      <ConfirmDialog
        open={toggling !== null}
        onClose={() => setToggling(null)}
        onConfirm={() => {
          if (toggling) {
            setActive.mutate({ id: toggling.id, active: !toggling.isActive });
          }
          setToggling(null);
        }}
        title={
          toggling ? `${toggling.isActive ? 'Désactiver' : 'Réactiver'} « ${toggling.name} »` : ''
        }
        description={
          toggling?.isActive
            ? 'Désactiver — aucune suppression physique. L’entrée reste consultable et pourra être réactivée.'
            : 'Réactiver cette entrée du référentiel ?'
        }
        confirmLabel={toggling?.isActive ? 'Désactiver' : 'Réactiver'}
        destructive={Boolean(toggling?.isActive)}
        loading={setActive.isPending}
      />
    </Card>
  );
}
